import { useState, useEffect } from "react";
import { PostAnAdFormSections } from "./sections/PostAnAdFormSections";
import { VehicleTestData } from "@/types/vehicleTest";

interface PostAnAdDetailsFormProps {
  initialData?: VehicleTestData;
  onChange: (data: VehicleTestData) => void;
  onNext: () => void;
  isEditMode?: boolean;
  isCreatingTest?: boolean;
  vehicleDetails?: { extraTireSet?: boolean };
  onVehicleDetailsChange?: (details: { extraTireSet?: boolean }) => void;
}

const SECTIONS = [
  "speed",
  "vibrations",
  "leaks",
  "noises",
  "gearbox",
  "crash",
  "ac",
  "dashboard",
  "windshield",
  "brakes",
  "tires",
];

export const PostAnAdDetailsForm = ({
  initialData,
  onChange,
  onNext,
  isEditMode = false,
  isCreatingTest = false,
  vehicleDetails,
  onVehicleDetailsChange,
}: PostAnAdDetailsFormProps) => {
  const [testData, setTestData] = useState<VehicleTestData>(initialData || {});

  // Determine which sections should be open initially
  const getInitialOpenSections = () => {
    if (!isEditMode) {
      return {
        speed: true,
        vibrations: false,
        leaks: false,
        noises: false,
        gearbox: false,
        crash: false,
        ac: false,
        dashboard: false,
        windshield: false,
        brakes: false,
        tires: false,
      };
    }

    // In edit mode, open sections that have data
    return {
      speed: !!initialData?.speed,
      vibrations: !!initialData?.vibrations?.problem,
      leaks: !!initialData?.leaks?.problem,
      noises: !!initialData?.noises?.problem,
      gearbox: !!initialData?.gearbox?.problem,
      crash: !!initialData?.crash?.problem,
      ac: !!initialData?.ac?.problem,
      dashboard: !!initialData?.dashboard?.problem,
      windshield: !!initialData?.windshield?.problem,
      brakes:
        !!initialData?.brakes?.frontEffectiveness &&
        !!initialData?.brakes?.rearEffectiveness,
      tires:
        !!initialData?.tires?.primary?.frontDepth ||
        !!initialData?.tires?.primary?.rearDepth,
    };
  };

  const [openSections, setOpenSections] = useState(getInitialOpenSections());

  useEffect(() => {
    onChange(testData);
  }, [testData, onChange]);

  const handleTestChange = (field: string, value: any) => {
    setTestData((prev) => ({ ...prev, [field]: value }));

    if (shouldAutoProgress(field, value)) {
      const currentIndex = SECTIONS.indexOf(field);
      if (currentIndex < SECTIONS.length - 1) {
        const nextSection = SECTIONS[currentIndex + 1];

        setOpenSections((prev) => ({
          ...prev,
          [field]: false,
          [nextSection]: true,
        }));
      }
    }
  };

  const shouldAutoProgress = (field: string, value: any) => {
    if (field === "speed" && value) {
      return true;
    }
    if (field === "vibrations" && value?.problem) {
      return (
        value.problem === "no" ||
        (value.problem === "yes" &&
          value.location &&
          value.location !== "Annat")
      );
    }
    if (field === "leaks" && value?.problem) {
      return (
        value.problem === "no" ||
        (value.problem === "yes" && value.source && value.source !== "Annat")
      );
    }
    if (field === "noises" && value?.problem) {
      return (
        value.problem === "no" ||
        (value.problem === "yes" && value.type && value.type !== "Annat")
      );
    }
    if (field === "gearbox" && value?.problem) {
      return (
        value.problem === "no" ||
        (value.problem === "yes" && value.issue && value.issue !== "Annat")
      );
    }
    if (field === "crash" && value?.problem) {
      return (
        value.problem === "no" ||
        value.problem === "unknown" ||
        (value.problem === "yes" && value.area && value.area !== "Annat")
      );
    }
    if (field === "ac" && value?.problem) {
      return (
        value.problem === "no" ||
        value.problem === "not_tested" ||
        (value.problem === "yes" && value.issue && value.issue !== "Annat")
      );
    }
    if (field === "dashboard" && value?.problem) {
      return (
        value.problem === "no" ||
        (value.problem === "yes" && value.issue && value.issue !== "Annat")
      );
    }
    if (field === "windshield" && value?.problem) {
      return value.problem === "no" || value.problem === "stone_chip";
    }
    if (field === "brakes" && value) {
      return value.frontEffectiveness && value.rearEffectiveness;
    }
    return false;
  };

  const handleEditSection = (section: string, event: React.MouseEvent) => {
    event.preventDefault();
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleNext = (section: string) => {
    const currentIndex = SECTIONS.indexOf(section);
    if (currentIndex < SECTIONS.length - 1) {
      const nextSection = SECTIONS[currentIndex + 1];
      setOpenSections((prev) => ({
        ...prev,
        [section]: false,
        [nextSection]: true,
      }));
    }
  };

  return (
    <div className="space-y-4 mb-4 w-full max-w-xl mx-auto">
      <PostAnAdFormSections
        testData={testData}
        openSections={openSections}
        handleTestChange={handleTestChange}
        handleEditSection={handleEditSection}
        handleNext={handleNext}
        onNext={onNext}
        isCreatingTest={isCreatingTest}
        isEditMode={isEditMode}
        vehicleDetails={vehicleDetails}
        onVehicleDetailsChange={onVehicleDetailsChange}
      />
    </div>
  );
};
